// A Markdown text, rendered and sanitised, and made live (today's
// markdown/MarkdownView.tsx, ported): its issue keys are links with a chip of
// the issue's state, its Mermaid diagrams are drawn once Mermaid has loaded
// (it loads on first need) and open in a zoom, every code block has Copy, and
// where the page can run a command, a shell block has Run. In-app links and
// `#anchor`s are followed without a page load.
import { Activity, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { useInRouterContext, useNavigate } from 'react-router-dom'
import type { BoardAttachment, Issue, Project } from '../api/types'
import { resolverOf } from '../plans/state'
import { useBoardPart } from '../store/context'
import { canInteract, useTabOwner, useTabScope } from '../workspace/context'
import { addBlockButtons, codeBlockText, copyCodeBlock } from './codeBlocks'
import { collectIssueKeys } from './issues'
import { cachedDiagram, renderDiagram, type ColorScheme, type DiagramResult } from './mermaid'
import { renderMarkdown, type RenderOptions } from './render'
import { USER_CONTENT_PREFIX } from './sanitize'
import type { ResolveIssue } from './types'
import { useColorScheme } from './useColorScheme'
import { ZoomOverlay, type ZoomTarget } from './ZoomOverlay'

export interface MarkdownProps extends Pick<RenderOptions, 'localImagesOnly' | 'linkTargets' | 'resolveUrl'> {
  source: string
  /** Extra classes on the box, which has `markdown-body`. */
  className?: string
  /**
   * Given, a block labelled `bash`, `sh` or `zsh` has a Run button, which
   * calls this with the block's text. It runs nothing itself.
   */
  onRun?: (command: string) => void
}

const NO_ATTACHMENTS: ReadonlyMap<string, BoardAttachment> = new Map()

const NO_PROJECTS: ReadonlyMap<string, Project> = new Map()
const NO_ISSUES: ReadonlyMap<string, Issue> = new Map()

/** The issue lookup the board's state gives, and the key names it reads keys in. */
function useIssueLinks(source: string): { prefixes: readonly string[]; resolveIssue: ResolveIssue | undefined; signature: string } {
  const projects = useBoardPart((state) => state.projects, NO_PROJECTS)
  const issues = useBoardPart((state) => state.issues, NO_ISSUES)
  const names = useMemo(() => [...projects.values()].map((project) => project.key), [projects])
  const namesSignature = names.join('\u0000')
  const keys = useMemo(() => collectIssueKeys(source, names), [source, namesSignature])
  const resolveIssue = useMemo<ResolveIssue | undefined>(() => {
    if (names.length === 0) return undefined
    const resolve = resolverOf(issues)
    return (key) => {
      const issue = resolve(key)
      return issue ? { title: issue.title, state: issue.state } : undefined
    }
  }, [issues, namesSignature])
  // What the lookup says about each key the source mentions: a new lookup that
  // gives the same answers must not render a long text again.
  let signature = namesSignature
  if (resolveIssue) {
    for (const key of keys) {
      const issue = resolveIssue(key)
      signature += issue ? `${key}\u0001${issue.state}\u0001${issue.title}\u0002` : `${key}\u0002`
    }
  }
  return { prefixes: names, resolveIssue, signature }
}

// ---------------------------------------------------------------- diagrams

function diagramBlocks(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>('.md-mermaid')]
}

function needsDrawing(block: HTMLElement, scheme: ColorScheme): boolean {
  const state = block.dataset.mdState
  return state === undefined || state === 'pending' || (state === 'ready' && block.dataset.mdTheme !== scheme)
}

function diagramSource(block: HTMLElement): string {
  return block.querySelector(':scope > .md-mermaid-source')?.textContent ?? ''
}

function showDiagram(block: HTMLElement, result: DiagramResult, scheme: ColorScheme): void {
  if ('svg' in result) {
    let host = block.querySelector<HTMLElement>(':scope > .md-mermaid-svg')
    if (!host) {
      host = document.createElement('div')
      host.className = 'md-mermaid-svg'
      block.prepend(host)
    }
    // Mermaid (securityLevel 'strict') has sanitised its own SVG.
    host.innerHTML = result.svg
    block.querySelector(':scope > .md-mermaid-error')?.remove()
    block.dataset.mdState = 'ready'
    block.dataset.mdTheme = scheme
  } else {
    block.querySelector(':scope > .md-mermaid-svg')?.remove()
    let note = block.querySelector<HTMLElement>(':scope > .md-mermaid-error')
    if (!note) {
      note = document.createElement('p')
      note.className = 'md-mermaid-error'
      note.setAttribute('role', 'note')
      block.append(note)
    }
    note.textContent = `Diagram not rendered: ${result.error}`
    block.dataset.mdState = 'error'
  }
}

// ---------------------------------------------------------------- anchors

function anchorTarget(root: HTMLElement, hash: string): HTMLElement | null {
  let id = hash.replace(/^#/, '')
  try {
    id = decodeURIComponent(id)
  } catch {
    // keep the raw fragment
  }
  if (!id) return null
  for (const name of [USER_CONTENT_PREFIX + id, id]) {
    for (const element of root.querySelectorAll<HTMLElement>('[id], a[name]')) {
      if (element.id === name || element.getAttribute('name') === name) return element
    }
  }
  return null
}

function scrollToAnchor(root: HTMLElement | null, hash: string, smooth: boolean, focus = true): boolean {
  const target = root && anchorTarget(root, hash)
  if (!target) return false
  const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  target.scrollIntoView?.({ block: 'start', behavior: smooth && !reduced ? 'smooth' : 'auto' })
  // Keyboard and screen-reader users continue from the section they jumped to.
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1')
  if (focus) target.focus({ preventScroll: true })
  return true
}

/** A path of this app: one slash, not `//host`. */
const OWN_PATH = /^\/(?![/\\])/

// ---------------------------------------------------------------- view

export function Markdown({ source, className, localImagesOnly = false, linkTargets = false, onRun, resolveUrl }: MarkdownProps) {
  const owner = useTabOwner()
  const scope = useTabScope()
  const inRouter = useInRouterContext()
  // The router is there for a mount or it is not, so the hook is called or not throughout.
  const routerNavigate = inRouter ? useNavigate() : null
  const rootRef = useRef<HTMLDivElement>(null)
  const written = useRef<string | null>(null)
  const initialHash = useRef(scope ? new URL(scope.path(), 'http://workspace.invalid').hash : typeof window === 'undefined' ? '' : window.location.hash)
  const scheme = useColorScheme()
  const [zoom, setZoom] = useState<ZoomTarget | null>(null)
  const { prefixes, resolveIssue, signature } = useIssueLinks(source)

  // The signature stands for the lookup and the prefixes.
  const attachments = useBoardPart((state) => state.attachments, NO_ATTACHMENTS)
  const fileHashes = useMemo(() => new Set([...attachments.values()].flatMap(a => a.old_sha256 ? [a.old_sha256] : [])), [attachments])
  const html = useMemo(() => renderMarkdown(source, { localImagesOnly, linkTargets, prefixes, resolveIssue, resolveUrl, fileHashes }), [source, localImagesOnly, linkTargets, signature, resolveUrl, fileHashes])
  const runnable = onRun !== undefined

  // Write the HTML, give its blocks their buttons, and draw every diagram
  // already rendered in this scheme before paint, so a re-render never
  // flashes a placeholder.
  useLayoutEffect(() => {
    const root = rootRef.current
    if (!root) return
    if (written.current !== html) {
      root.innerHTML = html
      written.current = html
    }
    addBlockButtons(root, runnable)
    for (const block of diagramBlocks(root)) {
      if (!needsDrawing(block, scheme)) continue
      const cached = cachedDiagram(diagramSource(block), scheme)
      if (cached) showDiagram(block, cached, scheme)
      else block.dataset.mdState = 'pending'
    }
    if (initialHash.current.length > 1 && scrollToAnchor(root, initialHash.current, false, canInteract(scope))) initialHash.current = ''
  }, [html, scheme, runnable])

  // Draw the rest once Mermaid has rendered them.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    let cancelled = false
    for (const block of diagramBlocks(root)) {
      if (!needsDrawing(block, scheme)) continue
      void renderDiagram(diagramSource(block), scheme).then((result) => {
        if (!cancelled && block.isConnected) showDiagram(block, result, scheme)
      })
    }
    return () => {
      cancelled = true
    }
  }, [html, scheme])

  const followLink = useCallback(
    (event: ReactMouseEvent, anchor: Element) => {
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      if (anchor.getAttribute('target') === '_blank') return
      const href = anchor.getAttribute('href') ?? ''
      if (href.startsWith('#')) {
        event.preventDefault()
        setZoom(null)
        scrollToAnchor(rootRef.current, href, true)
      } else if (OWN_PATH.test(href) && (scope || routerNavigate)) {
        event.preventDefault()
        setZoom(null)
        if (scope) scope.navigate(href)
        else routerNavigate!(href)
      }
    },
    [scope, routerNavigate],
  )

  const openDiagram = (block: Element | null) => {
    const svg = block?.querySelector(':scope > .md-mermaid-svg svg')
    if (svg instanceof SVGSVGElement) setZoom({ kind: 'diagram', svg })
  }

  const onClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    const root = rootRef.current
    const target = event.target
    if (!root || !(target instanceof Element)) return
    const copyButton = target.closest('button.md-code-copy')
    if (copyButton instanceof HTMLElement && root.contains(copyButton)) {
      void copyCodeBlock(copyButton)
      return
    }
    const runButton = target.closest('button.md-code-run')
    if (runButton && root.contains(runButton)) {
      const command = codeBlockText(runButton)
      if (onRun && command !== null && command.trim() !== '') onRun(command)
      return
    }
    const zoomButton = target.closest('button[data-md-zoom]')
    if (zoomButton && root.contains(zoomButton)) {
      openDiagram(zoomButton.closest('.md-mermaid'))
      return
    }
    const anchor = target.closest('a[href]')
    if (anchor && root.contains(anchor)) {
      followLink(event, anchor)
      return
    }
    const diagram = target.closest('.md-mermaid[data-md-state="ready"]')
    if (diagram && root.contains(diagram) && !window.getSelection()?.toString()) openDiagram(diagram)
  }

  return (
    <>
      <div ref={rootRef} className={className ? `markdown-body ${className}` : 'markdown-body'} data-md-live="" onClick={onClick} />
      <Activity mode={owner ? 'visible' : 'hidden'}>{zoom && <ZoomOverlay target={zoom} onClose={() => setZoom(null)} onLinkClick={followLink} />}</Activity>
    </>
  )
}
